import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import {
  LogIn,
  LogOut,
  Search,
  Download,
  RefreshCw,
  Inbox,
  Briefcase,
  Mail,
  Phone,
  ShieldAlert,
} from "lucide-react";
import { LOGO_URL } from "@/i18n/translations";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "agnm_admin_token";

function fmtDate(s) {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleString();
  } catch {
    return s;
  }
}

function toCsv(rows, columns) {
  const header = columns.map((c) => `"${c.label}"`).join(",");
  const body = rows
    .map((row) =>
      columns
        .map((c) => {
          const v = row[c.key];
          if (v === null || v === undefined) return "";
          return `"${String(v).replace(/"/g, '""')}"`;
        })
        .join(","),
    )
    .join("\n");
  return header + "\n" + body;
}

function downloadCsv(filename, csv) {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/* ----------------- Login ----------------- */
const LoginScreen = ({ onSuccess }) => {
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(TOKEN_KEY, data.token);
      toast.success("Welcome back");
      onSuccess(data.token);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.status === 401 ? "Wrong password" : "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center px-6" data-testid="admin-login">
      <form
        onSubmit={onSubmit}
        className="w-full max-w-md bg-surface border border-line rounded-md p-8 lg:p-10"
      >
        <div className="flex items-center gap-3 mb-7">
          <img src={LOGO_URL} alt="AGNM" className="h-10 w-auto" />
          <div>
            <div className="font-display text-xl uppercase tracking-[0.15em] font-bold">
              Admin
            </div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-white/40">
              AGNM Contracting LLC
            </div>
          </div>
        </div>
        <Label htmlFor="pw" className="text-[11px] uppercase tracking-[0.2em] text-white/60">
          Admin password
        </Label>
        <Input
          id="pw"
          type="password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="mt-1.5 bg-black border-line text-white"
          autoFocus
          data-testid="admin-password-input"
        />
        <Button
          type="submit"
          disabled={submitting}
          className="mt-6 w-full bg-lime-500 hover:bg-lime-400 text-black font-bold uppercase tracking-[0.15em] h-11 rounded-md"
          data-testid="admin-login-btn"
        >
          {submitting ? "Signing in..." : "Sign in"}
          {!submitting && <LogIn className="ml-2 h-4 w-4" />}
        </Button>
        <a
          href="/"
          className="block mt-6 text-center text-xs text-white/40 uppercase tracking-[0.2em] hover:text-white"
        >
          ← Back to site
        </a>
      </form>
      <Toaster position="top-center" richColors theme="dark" />
    </div>
  );
};

/* ----------------- Submissions Table ----------------- */
const Table = ({ rows, columns, emptyLabel }) => {
  if (rows.length === 0) {
    return (
      <div className="border border-dashed border-line rounded-md py-16 text-center text-white/50">
        <Inbox className="h-8 w-8 mx-auto mb-3 text-white/30" />
        {emptyLabel}
      </div>
    );
  }
  return (
    <div className="overflow-x-auto border border-line rounded-md">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-surface text-[11px] uppercase tracking-[0.15em] text-white/55">
            {columns.map((c) => (
              <th key={c.key} className="text-left px-4 py-3 font-semibold border-b border-line">
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr
              key={row.id || i}
              className={`${i % 2 === 1 ? "bg-white/[0.02]" : ""} hover:bg-lime-500/[0.04] transition`}
            >
              {columns.map((c) => (
                <td key={c.key} className="px-4 py-3 border-b border-line/40 align-top">
                  {c.render ? c.render(row) : row[c.key] ?? "—"}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

/* ----------------- Dashboard ----------------- */
const Dashboard = ({ token, onLogout }) => {
  const [contacts, setContacts] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [c, w] = await Promise.all([
        axios.get(`${API}/contact`, { headers }),
        axios.get(`${API}/worker-application`, { headers }),
      ]);
      setContacts(c.data);
      setWorkers(w.data);
    } catch (err) {
      if (err.response?.status === 401) {
        onLogout();
        return;
      }
      console.error(err);
      setError("Could not load submissions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const q = query.trim().toLowerCase();
  const filterFn = (obj) => {
    if (!q) return true;
    return Object.values(obj).some(
      (v) => v !== null && v !== undefined && String(v).toLowerCase().includes(q),
    );
  };

  const filteredContacts = contacts.filter(filterFn);
  const filteredWorkers = workers.filter(filterFn);

  const contactColumns = [
    { key: "created_at", label: "Date", render: (r) => fmtDate(r.created_at) },
    { key: "name", label: "Name" },
    {
      key: "email",
      label: "Contact",
      render: (r) => (
        <div className="space-y-1 text-xs">
          <a className="text-lime-400 hover:underline flex items-center gap-1" href={`mailto:${r.email}`}>
            <Mail className="h-3 w-3" />
            {r.email}
          </a>
          {r.phone && (
            <a className="text-white/70 hover:text-white flex items-center gap-1" href={`tel:${r.phone}`}>
              <Phone className="h-3 w-3" />
              {r.phone}
            </a>
          )}
        </div>
      ),
    },
    { key: "company", label: "Company / Farm" },
    {
      key: "audience",
      label: "Audience",
      render: (r) => (
        <span className="px-2 py-0.5 text-[10px] uppercase tracking-wider rounded bg-lime-500/15 border border-lime-500/40 text-lime-300">
          {r.audience}
        </span>
      ),
    },
    { key: "visa_type", label: "Visa" },
    {
      key: "message",
      label: "Message",
      render: (r) => (
        <div className="max-w-md whitespace-pre-wrap text-white/80">{r.message}</div>
      ),
    },
  ];

  const workerColumns = [
    { key: "created_at", label: "Date", render: (r) => fmtDate(r.created_at) },
    { key: "full_name", label: "Name" },
    {
      key: "email",
      label: "Contact",
      render: (r) => (
        <div className="space-y-1 text-xs">
          <a className="text-lime-400 hover:underline flex items-center gap-1" href={`mailto:${r.email}`}>
            <Mail className="h-3 w-3" />
            {r.email}
          </a>
          <a className="text-white/70 hover:text-white flex items-center gap-1" href={`tel:${r.phone}`}>
            <Phone className="h-3 w-3" />
            {r.phone}
          </a>
        </div>
      ),
    },
    { key: "country", label: "Country" },
    { key: "age", label: "Age" },
    { key: "languages", label: "Languages" },
    { key: "years_experience", label: "Yrs Exp" },
    { key: "crop_experience", label: "Crops / Work" },
    { key: "has_passport", label: "Passport" },
    { key: "prior_visa", label: "Prior H-2A" },
    { key: "available_from", label: "Available" },
    {
      key: "notes",
      label: "Notes",
      render: (r) => <div className="max-w-xs whitespace-pre-wrap text-white/80">{r.notes || "—"}</div>,
    },
  ];

  const exportContacts = () => {
    const csv = toCsv(filteredContacts, [
      { key: "created_at", label: "Date" },
      { key: "name", label: "Name" },
      { key: "email", label: "Email" },
      { key: "phone", label: "Phone" },
      { key: "company", label: "Company" },
      { key: "audience", label: "Audience" },
      { key: "visa_type", label: "Visa Type" },
      { key: "message", label: "Message" },
    ]);
    downloadCsv(`agnm-contacts-${new Date().toISOString().slice(0, 10)}.csv`, csv);
  };

  const exportWorkers = () => {
    const csv = toCsv(filteredWorkers, [
      { key: "created_at", label: "Date" },
      { key: "full_name", label: "Full Name" },
      { key: "email", label: "Email" },
      { key: "phone", label: "Phone" },
      { key: "country", label: "Country" },
      { key: "age", label: "Age" },
      { key: "languages", label: "Languages" },
      { key: "years_experience", label: "Years Experience" },
      { key: "crop_experience", label: "Crop Experience" },
      { key: "has_passport", label: "Has Passport" },
      { key: "prior_visa", label: "Prior Visa" },
      { key: "available_from", label: "Available From" },
      { key: "notes", label: "Notes" },
    ]);
    downloadCsv(`agnm-workers-${new Date().toISOString().slice(0, 10)}.csv`, csv);
  };

  return (
    <div className="min-h-screen bg-black text-white" data-testid="admin-dashboard">
      <header className="bg-surface border-b border-line sticky top-0 z-40 backdrop-blur">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 h-16 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="AGNM" className="h-9 w-auto" />
            <div className="hidden sm:block">
              <div className="font-display text-base uppercase tracking-[0.15em] font-bold leading-tight">
                Admin
              </div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/40">
                AGNM Contracting LLC
              </div>
            </div>
          </a>
          <div className="flex items-center gap-3">
            <button
              onClick={load}
              className="p-2 rounded-md text-white/70 hover:text-white hover:bg-white/5"
              aria-label="Refresh"
              data-testid="admin-refresh-btn"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            </button>
            <button
              onClick={onLogout}
              className="text-xs uppercase tracking-[0.15em] text-white/70 hover:text-white flex items-center gap-2 px-3 py-2 rounded-md hover:bg-white/5"
              data-testid="admin-logout-btn"
            >
              <LogOut className="h-3.5 w-3.5" />
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 lg:px-10 py-10">
        <div className="flex flex-wrap items-end gap-4 justify-between mb-6">
          <div>
            <h1 className="font-display text-3xl uppercase tracking-tight font-bold">
              Submissions
            </h1>
            <p className="text-white/55 text-sm mt-1">
              {contacts.length} contact{contacts.length === 1 ? "" : "s"} ·{" "}
              {workers.length} worker application{workers.length === 1 ? "" : "s"}
            </p>
          </div>
          <div className="relative w-full sm:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
            <Input
              placeholder="Search name, email, phone…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-9 bg-surface border-line text-white"
              data-testid="admin-search-input"
            />
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 rounded-md border border-red-500/40 bg-red-500/10 text-red-300 text-sm flex items-center gap-2">
            <ShieldAlert className="h-4 w-4" />
            {error}
          </div>
        )}

        <Tabs defaultValue="contacts" className="w-full">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
            <TabsList className="bg-surface border border-line">
              <TabsTrigger
                value="contacts"
                className="data-[state=active]:bg-lime-500 data-[state=active]:text-black"
                data-testid="admin-tab-contacts"
              >
                <Inbox className="h-4 w-4 mr-2" />
                Contacts ({contacts.length})
              </TabsTrigger>
              <TabsTrigger
                value="workers"
                className="data-[state=active]:bg-lime-500 data-[state=active]:text-black"
                data-testid="admin-tab-workers"
              >
                <Briefcase className="h-4 w-4 mr-2" />
                Worker Apps ({workers.length})
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="contacts">
            <div className="flex justify-end mb-3">
              <Button
                onClick={exportContacts}
                variant="outline"
                className="border-line text-white hover:bg-lime-500 hover:text-black bg-transparent text-xs uppercase tracking-[0.15em]"
                data-testid="admin-export-contacts"
              >
                <Download className="h-3.5 w-3.5 mr-2" />
                Export CSV
              </Button>
            </div>
            <Table
              rows={filteredContacts}
              columns={contactColumns}
              emptyLabel={query ? "No matches" : "No contact submissions yet"}
            />
          </TabsContent>

          <TabsContent value="workers">
            <div className="flex justify-end mb-3">
              <Button
                onClick={exportWorkers}
                variant="outline"
                className="border-line text-white hover:bg-lime-500 hover:text-black bg-transparent text-xs uppercase tracking-[0.15em]"
                data-testid="admin-export-workers"
              >
                <Download className="h-3.5 w-3.5 mr-2" />
                Export CSV
              </Button>
            </div>
            <Table
              rows={filteredWorkers}
              columns={workerColumns}
              emptyLabel={query ? "No matches" : "No worker applications yet"}
            />
          </TabsContent>
        </Tabs>
      </main>
      <Toaster position="top-center" richColors theme="dark" />
    </div>
  );
};

/* ----------------- Page ----------------- */
const AdminPage = () => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));

  // Validate token on mount
  useEffect(() => {
    if (!token) return;
    axios
      .get(`${API}/admin/me`, { headers: { Authorization: `Bearer ${token}` } })
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
      });
  }, [token]);

  const onLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  };

  if (!token) return <LoginScreen onSuccess={setToken} />;
  return <Dashboard token={token} onLogout={onLogout} />;
};

export default AdminPage;
